/* =========================================================================
 * FIND THE RESERVED PLATE. Pure: takes ImageData, returns a box and a quad.
 *
 * This is the rung 2 and rung 3 detector. The only difference between the two
 * rungs is what the HOUSING is: a generated sheet (rung 2) or an SR render
 * (rung 3). The maths is identical, which is why one module serves both.
 *
 * ⚠️ PORTED FROM find-plateau.ps1, UNCHANGED IN SUBSTANCE, and validated
 * against that script's own recorded output before anything was built on it.
 * On the Michigan v2 bezel the PowerShell reported area 51,209 box 370x151;
 * this logic reports 51,197 and the identical box. Same answer, 0.02% on area
 * from edge handling.
 *
 * A plate is BRIGHT and SMOOTH and not very saturated. Smoothness is the
 * load-bearing part: pavé is bright too and has enormous local variance at
 * every stone, while a machined pad has almost none.
 *
 * ⚠️ SATURATION IS WHY 13 OF 21 PLATES WERE MISSED. The original ceiling of
 * 0.16 is tuned for a SILVER pad. SR's housings include yellow and rose gold,
 * whose plates are legitimately saturated, and at 0.16 every one failed.
 * Measured 2026-09-19 on a freshly generated reserved-plate sheet:
 *
 *     sat ceiling 0.16  ->   8 of 21
 *     sat ceiling 0.30  ->  21 of 21
 *     sat ceiling 0.45  ->  21 of 21
 *
 * 0.45 is the default: it clears gold and rose gold with margin and stays far
 * below anything a coloured enamel or a printed mark would reach.
 * ========================================================================= */

export const PLATE_DEFAULTS = Object.freeze({
  lumMin: 185,
  satMax: 0.45,
  sdMax: 6.5,
  window: 7,
  minBlob: 400,
})

function fields(data, w, h) {
  const lum = new Float64Array(w * h)
  const sat = new Float64Array(w * h)
  for (let i = 0, p = 0; i < w * h; i++, p += 4) {
    const r = data[p], g = data[p + 1], b = data[p + 2]
    lum[i] = 0.299 * r + 0.587 * g + 0.114 * b
    const mx = Math.max(r, g, b), mn = Math.min(r, g, b)
    sat[i] = mx === 0 ? 0 : (mx - mn) / mx
  }
  return { lum, sat }
}

/** Separable box mean, so the window costs O(n) rather than O(n·k²). */
function boxMean(src, w, h, k) {
  const tmp = new Float64Array(w * h)
  const out = new Float64Array(w * h)
  const cl = (v, hi) => (v < 0 ? 0 : v > hi ? hi : v)
  for (let y = 0; y < h; y++) {
    const row = y * w
    let acc = 0
    for (let x = -k; x <= k; x++) acc += src[row + cl(x, w - 1)]
    for (let x = 0; x < w; x++) {
      tmp[row + x] = acc / (2 * k + 1)
      acc += src[row + cl(x + k + 1, w - 1)] - src[row + cl(x - k, w - 1)]
    }
  }
  for (let x = 0; x < w; x++) {
    let acc = 0
    for (let y = -k; y <= k; y++) acc += tmp[cl(y, h - 1) * w + x]
    for (let y = 0; y < h; y++) {
      out[y * w + x] = acc / (2 * k + 1)
      acc += tmp[cl(y + k + 1, h - 1) * w + x] - tmp[cl(y - k, h - 1) * w + x]
    }
  }
  return out
}

/**
 * The largest bright, smooth, low-saturation region, or null.
 *
 * Returns a bbox AND a quad from the per-row extents, because a bezel face is
 * photographed at a slight angle and its plate is a trapezium. Pasting a
 * rectangle into a trapezium is what makes a composite look stuck on.
 */
export function findPlate(imageData, opts = {}) {
  const o = { ...PLATE_DEFAULTS, ...opts }
  const { width: w, height: h, data } = imageData
  if (!w || !h) return null
  const k = Math.floor(o.window / 2)
  const { lum, sat } = fields(data, w, h)
  const mean = boxMean(lum, w, h, k)
  const sq = new Float64Array(w * h)
  for (let i = 0; i < w * h; i++) sq[i] = lum[i] * lum[i]
  const meanSq = boxMean(sq, w, h, k)

  const mask = new Uint8Array(w * h)
  for (let i = 0; i < w * h; i++) {
    if (lum[i] < o.lumMin || sat[i] > o.satMax) continue
    const sd = Math.sqrt(Math.max(0, meanSq[i] - mean[i] * mean[i]))
    if (sd < o.sdMax) mask[i] = 1
  }

  /* Largest connected component, 4-way, ITERATIVE. A recursive flood fill
   * blows the stack on a plate a few hundred pixels across. */
  const lab = new Int32Array(w * h)
  const stack = new Int32Array(w * h)
  let best = 0, bestSize = 0, cur = 0
  for (let i = 0; i < w * h; i++) {
    if (!mask[i] || lab[i]) continue
    cur++
    let size = 0, sp = 0
    stack[sp++] = i
    lab[i] = cur
    while (sp > 0) {
      const p = stack[--sp]
      size++
      const px = p % w
      if (px > 0 && mask[p - 1] && !lab[p - 1]) { lab[p - 1] = cur; stack[sp++] = p - 1 }
      if (px < w - 1 && mask[p + 1] && !lab[p + 1]) { lab[p + 1] = cur; stack[sp++] = p + 1 }
      if (p >= w && mask[p - w] && !lab[p - w]) { lab[p - w] = cur; stack[sp++] = p - w }
      if (p < w * h - w && mask[p + w] && !lab[p + w]) { lab[p + w] = cur; stack[sp++] = p + w }
    }
    if (size > bestSize) { bestSize = size; best = cur }
  }
  if (bestSize < o.minBlob) return null

  let minX = w, maxX = 0, minY = h, maxY = 0
  const rowMin = new Int32Array(h).fill(-1)
  const rowMax = new Int32Array(h).fill(-1)
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      if (lab[y * w + x] !== best) continue
      if (rowMin[y] < 0) rowMin[y] = x
      rowMax[y] = x
      if (x < minX) minX = x
      if (x > maxX) maxX = x
      if (y < minY) minY = y
      if (y > maxY) maxY = y
    }
  }
  /* The quad is read a little inside the first and last rows: those are the
   * blob's anti-aliased edge and are systematically narrower than the plate. */
  const inset = Math.max(1, Math.round((maxY - minY) * 0.08))
  const yTop = Math.min(maxY, minY + inset)
  const yBot = Math.max(minY, maxY - inset)
  return {
    area: bestSize,
    x: minX, y: minY, w: maxX - minX + 1, h: maxY - minY + 1,
    quad: {
      tl: [rowMin[yTop], yTop], tr: [rowMax[yTop], yTop],
      br: [rowMax[yBot], yBot], bl: [rowMin[yBot], yBot],
    },
  }
}

/**
 * Split a sheet into a grid of housing cells.
 *
 * ⚠️ THE GRID IS NOT GUESSED FROM THE FILENAME. Measured 2026-09-19: I assumed
 * 6/7/6 on a sheet that was actually 3 rows of 7 and scored the detector at
 * 7 of 19 against a real layout of 21. The wrong grid does not fail loudly, it
 * quietly reports a worse number, so the caller states rows and columns and
 * the UI shows the cells it used.
 */
export function gridCells(width, height, { rows, cols, top = 0.16, bottom = 0.96, pad = 6 }) {
  const cells = []
  const band = (bottom - top) / rows
  for (let r = 0; r < rows; r++) {
    const y0 = Math.round(height * (top + r * band))
    const y1 = Math.round(height * (top + (r + 1) * band))
    for (let c = 0; c < cols; c++) {
      const x0 = Math.round((width / cols) * c) + pad
      const x1 = Math.round((width / cols) * (c + 1)) - pad
      cells.push({ row: r + 1, col: c + 1, x: x0, y: y0, w: x1 - x0, h: y1 - y0 })
    }
  }
  return cells
}
